import { ThemeTokens } from '@acp/core-ui';
import {
    Button, Modal, ModalBody, ModalContent,
    ModalFooter, ModalHeader, ModalOverlay, Spacer, Text,
    useToast,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PropertyAddressCard } from './property-address-card';
import { markPropertyAsVacant } from '../service';

export function MarkPropertyAsVacantModal({
    propertyId,
    isOpen,
    onClose,
}: {
    propertyId: number;
    isOpen: boolean;
    onClose: () => void;
}) {
    const queryClient = useQueryClient();
    const toast = useToast();

    const mutation = useMutation({
        mutationFn: () => markPropertyAsVacant(propertyId),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['property', propertyId],
            });

            toast({
                title: 'Property marked as vacant',
                description: 'This property was successfully marked as vacant.',
                status: 'success',
                isClosable: true,
            });

            onClose();
        },
        onError: (error) => {
            toast({
                title: 'Failed to mark this property as vacant',
                description: error.message,
                status: 'error',
                isClosable: true,
            });
        },
    });

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    Are you sure you want to mark
                    this property as vacant?
                </ModalHeader>
                <ModalBody>
                    <PropertyAddressCard propertyId={propertyId} mb={6} />
                    <Text>
                        This action will vacate all current and previous tenancies
                        associated with this property.
                    </Text>
                </ModalBody>
                <ModalFooter mt={6}>
                    <Button
                        onClick={onClose}
                        borderColor={ThemeTokens.Defaults.borderColor}
                        color="muted.700"
                        bgColor="white"
                        variant="outline"
                        size="md"
                    >
                        Cancel
                    </Button>
                    <Spacer />
                    <Button
                        isLoading={mutation.isPending}
                        onClick={() => mutation.mutate()}
                        size="md"
                    >
                        Confirm
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
